"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormControl, FormMessage } from "@/components/ui/form";
import { Facebook, Instagram, Twitter, Youtube, ArrowRight, Loader, MapPin, Phone, Mail } from "lucide-react";
const SOCIAL_LINKS = [{
  icon: Facebook,
  href: "#",
  label: "Facebook"
}, {
  icon: Instagram,
  href: "#",
  label: "Instagram"
}, {
  icon: Twitter,
  href: "#",
  label: "Twitter"
}, {
  icon: Youtube,
  href: "#",
  label: "YouTube"
}];
function FooterLinks() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="237195" className="space-y-3">
        {[1, 2, 3, 4].map((_, i) => <div key={i} data-index={i} className="h-5 w-24 bg-muted animate-pulse rounded-sm" />)}
      </div>;
  }
  if (!menuItems || menuItems.length === 0) {
    return <ul className="space-y-3">
        {["Home", "Destinations", "Tours", "Contact"].map((item, i) => <li key={i} data-index={i}>
            <Link to="#" className="text-muted-foreground hover:text-primary transition-colors text-sm uppercase tracking-widest font-sans">
              {item}
            </Link>
          </li>)}
      </ul>;
  }
  return <ul className="space-y-3">
      {menuItems.map((item, i) => <li key={item.id} data-index={i}>
          <Link to={item.href || "#"} className="text-muted-foreground hover:text-primary transition-colors text-sm uppercase tracking-widest font-sans">
            {item.label}
          </Link>
        </li>)}
    </ul>;
}
function NewsletterForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-4 bg-primary/10 border border-primary/20 rounded-lg">
        <p className="text-primary text-sm font-default">{successMessage}</p>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-4">
      <FormField control={control} name="email" rules={{
      required: "Email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Invalid email address"
      }
    }} render={({
      field
    }) => <FormItem>
            <div className="relative flex items-center">
              <FormControl>
                <Input type="email" placeholder="YOUR EMAIL ADDRESS" className="bg-transparent border-0 border-b border-border rounded-none px-0 py-6 focus-visible:ring-0 focus-visible:border-primary text-foreground placeholder:text-muted-foreground uppercase tracking-widest text-xs font-sans" {...field} />
              </FormControl>
              <Button type="submit" variant="ghost" size="icon" disabled={isSubmitting} className="absolute right-0 hover:bg-transparent hover:text-primary text-muted-foreground transition-transform hover:translate-x-1">
                {isSubmitting ? <Loader className="size-5 animate-spin" /> : <ArrowRight className="size-5" />}
              </Button>
            </div>
            <FormMessage className="text-[10px] uppercase tracking-widest mt-2 font-sans" />
          </FormItem>} />
      {submitError && <p className="text-destructive text-[10px] uppercase tracking-widest font-sans">{submitError}</p>}
    </Form>;
}
export default function Footer() {
  const currentYear = new Date().getFullYear();
  return <section id="footer" className="bg-background text-foreground pt-24 pb-8 border-t border-border">
      <div className="container mx-auto px-4 max-w-7xl">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-12 lg:gap-8 mb-20">
          
          {}
          <div className="lg:col-span-4 flex flex-col items-start">
            <Link to="/" className="mb-8 inline-block">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1650028/img/voyage_logo_footer.png" alt="VOYAGE" className="h-7" />
            </Link>
            <p className="text-muted-foreground text-sm leading-relaxed mb-8 max-w-sm font-default">
              Curating extraordinary journeys for the modern explorer. Discover the world's most hidden gems with bespoke luxury and rugged adventure.
            </p>
            <div className="flex gap-4">
              {SOCIAL_LINKS.map((social, i) => <Link key={i} data-index={i} to={social.href} className="size-10 rounded-full bg-accent flex items-center justify-center text-muted-foreground hover:bg-primary hover:text-primary-foreground transition-all duration-300 shadow-sm" aria-label={social.label}>
                  <social.icon className="size-4" />
                </Link>)}
            </div>
          </div>

          {}
          <div className="lg:col-span-2">
            <h3 className="font-default text-sm font-semibold uppercase tracking-widest mb-6 text-foreground">
              Navigation
            </h3>
            <MenuProvider menu_id="51">
              <FooterLinks />
            </MenuProvider>
          </div>

          {}
          <div className="lg:col-span-3">
            <h3 className="font-default text-sm font-semibold uppercase tracking-widest mb-6 text-foreground">
              Top Destinations
            </h3>
            <ul className="space-y-4 text-sm text-muted-foreground font-default">
              <li className="flex items-start gap-3">
                <MapPin className="size-5 text-primary shrink-0 mt-0.5" />
                <span>Patagonia Expedition, Chile</span>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="size-5 text-primary shrink-0" />
                <span>+1 (800) VOYAGE-01</span>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="size-5 text-primary shrink-0" />
                <span>concierge@voyage.travel</span>
              </li>
            </ul>

            <h3 className="font-default text-sm font-semibold uppercase tracking-widest mt-8 mb-4 text-foreground">
              Concierge Hours
            </h3>
            <div className="space-y-1 text-sm text-muted-foreground font-default">
              <p>Mon-Fri: 9am - 8pm EST</p>
              <p>Sat: 10am - 4pm EST</p>
            </div>
          </div>

          {}
          <div className="lg:col-span-3">
            <h3 className="font-default text-sm font-semibold uppercase tracking-widest mb-6 text-foreground">
              The Explorer's Journal
            </h3>
            <p className="text-muted-foreground text-sm mb-6 font-default">
              Join our inner circle for exclusive destination guides, early access to new tours, and travel inspiration.
            </p>
            <FormProvider formKey="newsletter_signup_home" formId="newsletter_signup_home" sectionName="footer" defaultValues={{
            email: ""
          }} submitText="Subscribe" successMessage="Welcome to the journey. Check your inbox.">
              <NewsletterForm />
            </FormProvider>
          </div>

        </div>

        {}
        <div className="pt-8 relative">
          <Separator className="absolute top-0 left-0 w-full bg-border h-px" />
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-muted-foreground text-[10px] uppercase tracking-widest font-sans">
              &copy; {currentYear} VOYAGE TRAVEL AGENCY. ALL RIGHTS RESERVED.
            </p>
            <div className="flex gap-6 text-[10px] uppercase tracking-widest text-muted-foreground font-sans">
              <Link to="#" className="hover:text-primary transition-colors">Privacy Policy</Link>
              <Link to="#" className="hover:text-primary transition-colors">Terms of Service</Link>
            </div>
          </div>
        </div>
      </div>
    </section>;
}